import isEqual from 'lodash.isequal'
import { facilities } from '@/externals/gyym/user'
import {
  RecommendedFacility,
  CampaignFacility,
  FacilityId,
  SearchHistory,
  AreasByPref,
  Area,
} from './types'
import {
  ListParams,
  FacilityDetails,
  SearchCountParams,
} from '@/externals/gyym/user/facilities'
import { PrefectureId } from '@/externals/gyym/user/prefectures'
import { PagerParams } from '@/externals/gyym'
import { Activity, ActivityId, sortActivities } from '../Activities'
import { groupAreaByPrefecture } from './utils'
import { FACILITY_SEARCH_HISTORY_KEY, initialSearchParams } from './constants'
import isLocalStorageAvailable from '@/utils/isLocalStorageAvailable'
import {
  initializeFetchMasterData,
  cacheSearchMasterData,
} from '@/store/modules/search/types'
import store from '@/store'
import { Keys } from '@/store/modules/search/getters'
import { AreaId } from '@/externals/gyym/user/areas'
import { cacheableAction } from '@/utils/cacheableAction'

export const listFacilities = async (params: ListParams) => {
  const { data, meta } = await facilities.list(params)

  return {
    data: data.map(({ activities, ...facility }) => ({
      ...facility,
      // 施設のアクティビティをを並べ替える
      activities: sortActivities(activities),
    })),
    meta,
  }
}

export const listOnlineFacilities = async (params?: ListParams) => {
  const { data, meta } = await facilities.listOnline(params)

  return {
    data: data.map(({ activities, ...facility }) => ({
      ...facility,
      isOnline: true,
      // 施設のアクティビティをを並べ替える
      activities: sortActivities(activities),
    })),
    meta,
  }
}

export const listRecommendedFacilities = async (): Promise<
  RecommendedFacility[]
> => {
  const data = await facilities.listRecommended()
  // ランダムの並び順にする
  // @see https://www.30secondsofcode.org/js/s/shuffle/
  let m = data.length
  while (m) {
    const i = Math.floor(Math.random() * m--)
    ;[data[m], data[i]] = [data[i], data[m]]
  }
  return data.map(({ activities, ...facility }) => ({
    ...facility,
    // 施設のアクティビティをを並べ替える
    activities: sortActivities(activities),
  }))
}

export const listCampaignFacilities = async (): Promise<CampaignFacility[]> => {
  const data = await facilities.listCampaign()
  // ランダムの並び順にする
  // @see https://www.30secondsofcode.org/js/s/shuffle/
  let m = data.length
  while (m) {
    const i = Math.floor(Math.random() * m--)
    ;[data[m], data[i]] = [data[i], data[m]]
  }
  return data.map(({ activities, ...facility }) => ({
    ...facility,
    // 施設のアクティビティをを並べ替える
    activities: sortActivities(activities),
  }))
}

/**
 * @deprecated use facilitiesMetadata
 */
export const searchFacilities = async () => {
  return await facilities.search()
}

const facilitiesMetadataAction = cacheableAction(async () => {
  const { activities, ...rest } = await facilities.searchMetadata()
  return {
    ...rest,
    // 施設のアクティビティをを並べ替える
    activities: sortActivities(activities),
  }
})

export const facilitiesMetadata = facilitiesMetadataAction.withCache(
  'facilitiesMetadata_v1',
  {
    expiresAfter: 10 * 60 * 1000,
  }
)

export const useFacilitiesMetadataStore = () => {
  const isFetched = (): boolean => store.getters[Keys.IS_MASTER_FETCHED]

  const isFetching = (): boolean => store.getters[Keys.IS_MASTER_FETCHING]

  const fetch = async (_shouldWait: boolean = false) => {
    if (isFetched()) return

    store.commit(initializeFetchMasterData())
    const { activities, areas } = await facilitiesMetadata()
    store.commit(cacheSearchMasterData({ activities, areas }))
  }

  const getActivities = (): Activity[] =>
    store.getters[Keys.GET_SEARCH_ACTIVITY_LIST]

  const getActivity = (activityId: ActivityId): Activity | undefined => {
    const activities = getActivities()
    return activities.find(({ id }) => id === activityId)
  }

  const getAreasByPref = (): AreasByPref[] =>
    store.getters[Keys.GET_SEARCH_AREA_LIST]

  const getPrefecture = (
    prefectureId: PrefectureId
  ): AreasByPref | undefined => {
    const areasByPref = getAreasByPref()
    const pref = areasByPref.find(({ id }) => id === prefectureId)
    return pref
  }

  const getAreas = (): Area[] =>
    getAreasByPref().reduce(
      (acc, { areas }) => [...acc, ...areas],
      [] as Area[]
    )

  const getArea = (areaId: AreaId): Area | undefined => {
    const areas = getAreas()
    return areas.find(({ id }) => id === areaId)
  }

  return {
    fetch,
    isFetching,
    isFetched,
    getActivities,
    getActivity,
    getAreasByPref,
    getPrefecture,
    getAreas,
    getArea,
  }
}

export const facilitiesSearchCount = async (params: SearchCountParams) => {
  return await facilities.searchCount(params)
}

export const searchFacilitiesByArea = async () => {
  const { areas } = await facilitiesMetadata()
  return await groupAreaByPrefecture(areas || [])
}

export const getFacility = async (id: FacilityId): Promise<FacilityDetails> => {
  const { activities, programs, ...facility } = await facilities.get(id)

  return {
    ...facility,
    // 施設のアクティビティをを並べ替える
    activities: sortActivities(activities),
    programs: programs.map(({ activities: programActivities, ...program }) => ({
      ...program,
      // 施設のプログラムのアクティビティをを並べ替える
      activities: sortActivities(programActivities),
    })),
  }
}

export const loadMoreFacilityPrograms = async (id: FacilityId) => {
  return await facilities.loadMorePrograms(id)
}

export const getFacilityReviews = async (
  id: FacilityId,
  pager?: PagerParams
) => {
  const { data, meta } = await facilities.listReviews(id, pager)
  return { data, meta }
}

export const listFacilityTimetables = async (facilityId: FacilityId) => {
  return await facilities.listTimetables(facilityId)
}

// 件数が一定ではないので内部利用のみ (exportはしない)
const getAllSearchHistories = (): SearchHistory[] => {
  // localStorageが利用できない場合は履歴を空で返す
  if (!isLocalStorageAvailable()) return []

  try {
    return JSON.parse(localStorage.getItem(FACILITY_SEARCH_HISTORY_KEY) || '[]')
  } catch (e) {
    return []
  }
}

/**
 * 施設名検索履歴を10件取得する
 */
export const getNameSearchHistories = (): string[] => {
  const histories = getAllSearchHistories()
  return histories
    .filter((history) => history.facilityName)
    .map((history) => history.facilityName as string)
    .slice(0, 10)
}

/**
 * 検索履歴を10件取得する
 */
export const getSearchHistories = (): SearchHistory[] => {
  const histories = getAllSearchHistories()
  return histories.slice(0, 10)
}

export const setSearchHistory = (params: Partial<SearchHistory>): void => {
  const history: SearchHistory = { ...initialSearchParams, ...params }

  // 初期値と同じ場合は何もしない
  if (isEqual(initialSearchParams, history)) return

  // localStorageが利用できない場合は何もしない
  if (!isLocalStorageAvailable()) return

  const histories = getAllSearchHistories()

  // 新しいhistoryをprependする
  const newHistories = [history, ...histories].reduce((acc, value) => {
    const countHistoryWithFacilityName = acc.filter(
      (history) => history.facilityName
    ).length

    // 施設名検索を含む検索履歴を最大10件保持
    // ローカルストレージ上は10件以上の履歴が保持される可能性があるので注意
    return countHistoryWithFacilityName >= 10 ? acc : [...acc, value]
  }, [] as SearchHistory[])

  try {
    localStorage.setItem(
      FACILITY_SEARCH_HISTORY_KEY,
      JSON.stringify(newHistories)
    )
  } catch (e) {
    // NOOP
  }
}

export const getCityNameByCode = (cityCode: number) => {
  return facilities.getCityNameByCode(cityCode)
}
